import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosRDV, axiosSousProfile, axiosUsers } from "../../../api/axios";
import ENDPOINTS, { SOUS_PROFILE_PATHS } from "../../../api/endpoints";
import Cookies from "cookies-js"; 


const unwrapUser = (payload) => {
  if (payload && typeof payload === "object" && "data" in payload) return payload.data;
  return payload;
};

// Normalise l'erreur
const toRejection = (err, fallback) => {
  return (
    err?.response?.data?.message ??
    (typeof err?.response?.data === "string" ? err.response.data : undefined) ??
    err?.message ??
    fallback
  );
};

/** 🔹 Charger un utilisateur par ID (depuis ms-users) */
const resolveId = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value !== "object") return null;

  return (
    value.id ??
    value._id ??
    value.userId ??
    value.sub ??
    value.pgId ??
    value.uuid ??
    value.spId ??
    value.subprofileId ??
    value.subProfileId ??
    value.profileId ??
    null
  );
};

const splitName = (profile) => {
  const fullName =
    profile?.fullName ||
    profile?.name ||
    [profile?.firstName, profile?.lastName].filter(Boolean).join(" ");
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return {
    firstName: profile?.firstName ?? parts[0] ?? "",
    lastName: profile?.lastName ?? parts.slice(1).join(" "),
    fullName,
  };
};

const normalizeSubprofileAsUser = (profile, fallbackId) => {
  if (!profile || typeof profile !== "object") return null;

  const id = resolveId(profile) ?? fallbackId;
  if (!id) return null;

  const names = splitName(profile);

  return {
    ...profile,
    id,
    subprofileId: profile.subprofileId ?? profile.subProfileId ?? id,
    firstName: names.firstName,
    lastName: names.lastName,
    fullName: names.fullName,
    role: profile.role ?? "patient",
    avatarUrl: profile.avatarUrl ?? profile.avatar ?? null,
    isSubprofile: true,
  };
};

const getActiveSubprofileId = (session) => {
  if (session?.mode !== "subprofile") return null;
  return (
    resolveId(session?.actingAs) ??
    resolveId(session?.profile) ??
    resolveId(session)
  );
};

const getCachedSubprofile = (state, id) => {
  const byId = state?.sousProfiles?.byId?.[id];
  if (byId) return byId;

  return state?.sousProfiles?.items?.find?.(
    (profile) => String(resolveId(profile)) === String(id)
  );
};

/** Charger un utilisateur par ID, ou le sous-profile actif quand le switch est actif. */
export const fetchUserById = createAsyncThunk(
  "user/fetchUserById",
  async (userId, { getState, rejectWithValue, signal }) => {
    try {
      const target = userId && typeof userId === "object" ? userId : {};
      const requestedUserId = target.userId ?? userId;
      const requestedSubprofileId = target.subprofileId ?? null;
      const requestedPrincipalUserId = target.principalUserId ?? null;
      if (!requestedUserId) return rejectWithValue("User ID is required");

      const state = getState?.();
      const activeSubprofileId = getActiveSubprofileId(
        state?.sousProfiles?.actingSession
      );
      const subprofileId = requestedSubprofileId || activeSubprofileId;
      const principalUserId = requestedPrincipalUserId ||
        (subprofileId ? resolveId(state?.sousProfiles?.actingSession?.profile) : null);

      if (
        subprofileId &&
        String(subprofileId) === String(requestedUserId)
      ) {
        const cachedProfile = normalizeSubprofileAsUser(
          getCachedSubprofile(state, subprofileId),
          subprofileId
        );

        const sessionProfile = normalizeSubprofileAsUser(
          state?.sousProfiles?.actingSession?.profile,
          subprofileId
        );

        if (cachedProfile) {
          return {
            ...sessionProfile,
            ...cachedProfile,
            id: subprofileId,
            subprofileId,
          };
        }

        try {
          const { data } = await axiosSousProfile.get(
            SOUS_PROFILE_PATHS.GET_BY_ID(subprofileId),
            {
              signal,
              withCredentials: true,
            }
          );

          const subprofile = normalizeSubprofileAsUser(unwrapUser(data), subprofileId);
          if (subprofile) {
            return {
              ...sessionProfile,
              ...subprofile,
              id: subprofileId,
              subprofileId,
            };
          }
        } catch (err) {
          if (err?.name === "CanceledError") throw err;
          if (sessionProfile) return sessionProfile;
          throw err;
        }

        return rejectWithValue("Subprofile data not found");
      }

      const { data } = await axiosUsers.get(ENDPOINTS.USERS.BY_ID(requestedUserId), {
        signal,
        withCredentials: true,
      });

      const user = unwrapUser(data);
      if (!user || (typeof user === "object" && Object.keys(user).length === 0)) {
        return rejectWithValue("User data not found");
      }
      return principalUserId && subprofileId
        ? { ...user, patientId: principalUserId, subprofileId, isSubprofile: true }
        : user;
    } catch (err) {
      if (err?.name === "CanceledError") return rejectWithValue("Request canceled");
      return rejectWithValue(toRejection(err, "Erreur lors du chargement du profil"));
    }
  }
);

/** 🔹 Charger tous les utilisateurs (patients) */
export const fetchUsers = createAsyncThunk(
  "user/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosRDV.get("/my-patients", {
        withCredentials: true,
        params: { includeAll: true },
      });
      const rows = Array.isArray(res.data) ? res.data : res.data?.data || [];

      return rows.map((p) => {
        const subprofileId = p.subprofileId ?? p.subProfileId ?? null;
        const source = subprofileId || p.profileScope === "subprofile"
          ? "subprofile"
          : p.source || "medilink";

        return {
        id: String(p.id ?? p._id ?? p.userId),
        patientId: p.patientId ?? p.raw?.linkedUserId ?? p.id ?? null,
        subprofileId,
        effectiveProfileId: p.effectiveProfileId ?? p.id ?? null,
        profileScope:
          p.profileScope || (p.subprofileId ? "subprofile" : "principal"),
        identifier: p.identifier || "",
        firstName: p.firstName || "",
        lastName: p.lastName || "",
        fullName: `${p.firstName || ""} ${p.lastName || ""}`.trim(),
        email: p.email || "",
        phone: p.phone || "",
        dateOfBirth: p.dateOfBirth || p.birthdate || p.birthDate || "",
        birthdate: p.dateOfBirth || p.birthdate || p.birthDate || "",
        birthDate: p.dateOfBirth || p.birthdate || p.birthDate || "",
        gender: p.gender || "",
        role: p.role || "patient",
        avatarUrl: p.avatarUrl || p.avatar || null,
        source,
        raw: p,
        };
      });
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Erreur lors du chargement des patients";
      return rejectWithValue(message);
    }
  }
);

export const fetchAllMedilinkPatients = createAsyncThunk(
  "user/fetchAllMedilinkPatients",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosUsers.get(ENDPOINTS.USERS.ALL, {
        withCredentials: true,
        params: { view: "appointment-patients" },
      });
      const rows = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

      return rows
        .filter((patient) =>
          !patient?.role || String(patient.role).toLowerCase() === "patient"
        )
        .map((patient) => {
          const id = patient.id ?? patient._id ?? patient.userId;
          return id
            ? { ...patient, id: String(id), source: "medilink" }
            : null;
        })
        .filter(Boolean);
    } catch (err) {
      return rejectWithValue(toRejection(err, "Erreur lors du chargement des patients"));
    }
  }
);
/** 🔹 Mettre à jour un utilisateur */
/** Mettre à jour un utilisateur (depuis ms-users) */
export const updateUser = createAsyncThunk(
  "user/updateUser",
  async ({ userId, values }, { getState, rejectWithValue, signal }) => {
    try {
      if (!userId) return rejectWithValue("User ID is required");

      const state = getState?.();
      const activeSubprofileId = getActiveSubprofileId(
        state?.sousProfiles?.actingSession
      );
      const isActiveSubprofileTarget =
        activeSubprofileId && String(activeSubprofileId) === String(userId);
      const isFormData = typeof FormData !== "undefined" && values instanceof FormData;
      const config = { signal, headers: {} };
      if (isFormData) config.headers["Content-Type"] = "multipart/form-data";

      const { data } = isActiveSubprofileTarget
        ? await axiosSousProfile.put(SOUS_PROFILE_PATHS.UPDATE(userId), values, config)
        : await axiosUsers.put(ENDPOINTS.USERS.UPDATE(userId), values, config); // ✅ axiosUsers
      const updatedUser = unwrapUser(data);

      if (updatedUser && typeof updatedUser === "object" && !Array.isArray(updatedUser)) {
        if (isActiveSubprofileTarget) {
          return (
            normalizeSubprofileAsUser(
              {
                ...(!isFormData && values && typeof values === "object" ? values : {}),
                ...updatedUser,
              },
              userId
            ) || { id: userId, ...values, isSubprofile: true }
          );
        }

        return {
          ...(!isFormData && values && typeof values === "object" ? values : {}),
          ...updatedUser,
          id: resolveId(updatedUser) ?? userId,
        };
      }

      if (!isFormData && values && typeof values === "object") {
        return {
          ...values,
          id: userId,
          ...(isActiveSubprofileTarget ? { subprofileId: userId, isSubprofile: true } : {}),
        };
      }

      return {
        id: userId,
        ...(isActiveSubprofileTarget ? { subprofileId: userId, isSubprofile: true } : {}),
      };
    } catch (err) {
      if (err?.name === "CanceledError") return rejectWithValue("Request canceled");
      return rejectWithValue(toRejection(err, "Erreur lors de la mise à jour"));
    }
  }
);

